/**
 * TypeScript types for the Persona Wizard backend API (P1.T6).
 *
 * Mirrors `backend/app/schemas/persona_wizard.py`. Field names are
 * snake_case to match the backend's JSON shape — wizard endpoints
 * are new in P1.T5 so we don't pay the legacy camelCase translation
 * tax that `usePersonas` carries for the older /api/personas surface.
 */

import type { ImportOrigin } from '@/pages/PersonaWorkshop/importOriginText'

// ── Wizard ──────────────────────────────────────────────────────────────────

export const STARTER_RECIPES: Record<string, string> = {
  bakery: 'Bakery / catering',
  salon: 'Salon / spa',
  tutor: 'Tutor',
  support: 'Customer support',
  clinic: 'Clinic',
  concierge: 'Generic concierge',
}

export interface WizardAnswers {
  business_description: string
  customers: string
  personality: string
  languages: string[]
  starter_recipe: string | null
  persona_name: string | null
}

export interface PersonaBundleBlock {
  category: string
  content: string
  position?: number
}

export interface PersonaBundleResponse {
  id: string
  persona_id: string
  version: number
  is_current: boolean
  blocks: PersonaBundleBlock[]
  default_skills: { name: string }[]
  auto_skills: { name: string }[]
  tool_bindings: Record<string, unknown>
  workflow_bindings: unknown[]
  channel_overrides: Record<string, unknown>
  policy: Record<string, unknown>
  variable_values: Record<string, string>
  created_at?: string | null
  created_by?: string | null
  /** users.display_name resolved server-side on GET list/single only. */
  created_by_name?: string | null
  /** Set on a version the portability import wrote (SA-1473); null/absent otherwise. */
  import_origin?: ImportOrigin | null
}

export interface WizardResponse {
  persona_id: string
  bundle: PersonaBundleResponse
  explanation: string | null
}

// ── Simulate ────────────────────────────────────────────────────────────────

export interface SimulateRequest {
  user_message: string
  turn_signals?: Record<string, unknown>
}

export interface SimulateResponse {
  rendered_prompt: string
  target_format: 'xml' | 'markdown'
  assistant_reply: string
  layers_rendered: string[]
  token_estimate: number
}
